import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  Form as BootstrapForm, InputGroup, Button, Row, Col,
  Card, Container, Badge
} from 'react-bootstrap';
import { FaBarcode, FaRulerCombined, FaFont, FaLayerGroup, FaSave, FaEye, FaMobileAlt, FaTag } from 'react-icons/fa';

const BarcodeSettingsForm = ({ items, onSubmit, initialValues: propInitialValues }) => {

  // Default Configuration
  const defaultValues = {
    settingName: '',
    bcid: 'code128',
    text: '',
    scale: 3,
    height: 20,
    includetext: true,
    textxalign: 'center',
    textsize: 8,
    backgroundcolor: 'ffffff',
    paddingtop: 5,
    paddingbottom: 5,
    textyoffset: 3,
    itemNameFontSize: 12,
    companyNameFontSize: 12,
    priceFontSize: 12,
    itemNameX: '50%',
    itemNameY: '10',
    companyNameX: '90%',
    companyNameY: '100%',
    priceX: '5%',
    priceY: '100%',
    paperType: "sheet",
    pageSize: "A4",
    rollWidth: 50,
    labelWidth: 38,
    labelHeight: 25,
    padding: 2,
    gap: 3,
    gridColumns: 1,
    orientation: "portrait",
    showPrice: true,
    showBatteryHealth: true,
    deviceMode: 'both',
  };

  const initialValues = { ...defaultValues, ...propInitialValues };

  const validationSchema = Yup.object({
    settingName: Yup.string().required('Setting Name is required'),
    bcid: Yup.string().required('Required'),
    scale: Yup.number().min(1).max(10).required('Required'),
    height: Yup.number().min(5).max(100).required('Required'),
    labelWidth: Yup.number().required('Required'),
    labelHeight: Yup.number().required('Required'),
  });

  // Helper to determine dropdown state from boolean values
  const getDeviceMode = (values) => {
    if (values.showBatteryHealth && values.showPrice) return 'both';
    if (values.showBatteryHealth && !values.showPrice) return 'battery_only';
    return 'price_only';
  };

  return (
    <Container fluid className="p-0">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}

      >
        {({ values, isSubmitting, setFieldValue }) => (
          <Form>

            {/* --- TOP HEADER --- */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h4 className="fw-bold text-dark mb-1">Barcode Configuration</h4>
                <p className="text-muted small mb-0">Manage label dimensions, layout, and data visibility.</p>
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="px-4 fw-bold shadow-sm"
                style={{ backgroundColor: '#001c3a', borderColor: '#001c3a' }}
              >
                <FaSave className="me-2" /> Save Configuration
              </Button>
            </div>

            <Row className="g-4">
              {/* LEFT COLUMN: CORE SETTINGS */}
              <Col lg={8}>

                {/* 1. GENERAL IDENTITY */}
                <Card className="border-0 shadow-sm mb-4">
                  <Card.Header className="bg-white border-bottom py-3">
                    <h6 className="fw-bold m-0 d-flex align-items-center text-primary">
                      <FaBarcode className="me-2" /> General Identity
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Row className="g-3">
                      <Col md={6}>
                        <BootstrapForm.Group>
                          <BootstrapForm.Label className="small fw-bold text-muted">Configuration Name</BootstrapForm.Label>
                          <Field as={BootstrapForm.Control} name="settingName" placeholder="e.g. Standard 38x25mm" className="fw-bold" />
                          <ErrorMessage name="settingName" component="div" className="text-danger x-small mt-1" />
                        </BootstrapForm.Group>
                      </Col>
                      <Col md={6}>
                        <BootstrapForm.Group>
                          <BootstrapForm.Label className="small fw-bold text-muted">Barcode Symbology</BootstrapForm.Label>
                          <Field as={BootstrapForm.Select} name="bcid" className="form-select">
                            <option value="code128">Code 128 (Standard)</option>
                            <option value="code39">Code 39 (Legacy)</option>
                            <option value="ean13">EAN 13 (Retail)</option>
                            <option value="upca">UPC-A</option>
                            <option value="qrcode">QR Code (Side-by-Side)</option>
                          </Field>
                        </BootstrapForm.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* 2. CONTENT STRATEGY (THE REQUESTED FIX) */}
                <Card className="border-0 shadow-sm mb-4">
                  <Card.Header className="bg-white border-bottom py-3">
                    <h6 className="fw-bold m-0 d-flex align-items-center text-success">
                      <FaEye className="me-2" /> Content Strategy
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Row className="g-4 align-items-start">
                      {/* Global Price Toggle */}
                      <Col md={5} className="border-end">
                        <div className="d-flex align-items-center mb-2">
                          <FaTag className="text-muted me-2" />
                          <label className="small fw-bold text-dark text-uppercase">Universal Settings</label>
                        </div>
                        <div className="bg-light p-3 rounded-3 border">
                          <BootstrapForm.Check
                            type="switch"
                            id="showPrice-switch"
                            label={<span className="fw-bold text-dark">Print Price on Label</span>}
                            checked={values.showPrice}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setFieldValue('showPrice', isChecked);
                              // If turning off price, ensure B/H is handled if in 'both' mode
                              if (!isChecked && values.showBatteryHealth) {
                                // Intentionally left to allow 'battery_only' state
                              }
                            }}
                          />
                          <div className="text-muted x-small mt-1 ps-1">
                            Applies to all items (Devices & Accessories).
                          </div>
                        </div>
                      </Col>

                      {/* Device Specific Dropdown */}
                      <Col md={7}>
                        <div className="d-flex align-items-center mb-2">
                          <FaMobileAlt className="text-muted me-2" />
                          <label className="small fw-bold text-dark text-uppercase">Device Configuration <Badge bg="info" className="x-small ms-1">OVERRIDE</Badge></label>
                        </div>
                        <BootstrapForm.Group>
                          <BootstrapForm.Select
                            value={values.deviceMode}
                            onChange={(e) => {
                              const mode = e.target.value;
                              setFieldValue('deviceMode', mode);
                              if (mode === 'both') {
                                setFieldValue('showBatteryHealth', true);
                                setFieldValue('showPrice', true); // Enforce Price
                              } else if (mode === 'price_only') {
                                setFieldValue('showBatteryHealth', false);
                                // Don't touch showPrice (respect global)
                              } else if (mode === 'battery_only') {
                                setFieldValue('showBatteryHealth', true);
                                setFieldValue('showPrice', false); // Override Global
                              }
                            }}
                            className="form-select border-primary border-opacity-25   bg-opacity-10 fw-bold  "
                          >
                            <option value="price_only">Show Price Only (Hide Battery Health)</option>
                            <option value="both">Show Price & Battery Health</option>
                            <option value="battery_only">Show Battery Health Only (Hide Price)</option>
                          </BootstrapForm.Select>
                          <BootstrapForm.Text className="text-muted x-small d-block mt-1">
                            Control data density for smartphone labels. Selecting "Battery Only" will hide the price globally for this print job.
                          </BootstrapForm.Text>
                        </BootstrapForm.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* 3. DIMENSIONS & PAPER */}
                <Card className="border-0 shadow-sm mb-4">
                  <Card.Header className="bg-white border-bottom py-3">
                    <h6 className="fw-bold m-0 d-flex align-items-center text-info">
                      <FaRulerCombined className="me-2" /> Paper & Dimensions
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Row className="g-3 mb-4">
                      <Col md={4}>
                        <BootstrapForm.Label className="small fw-bold text-muted">Paper Source</BootstrapForm.Label>
                        <Field as={BootstrapForm.Select} name="paperType" className="form-select">
                          <option value="sheet">A4 / Letter Sheet</option>
                          <option value="roll">Thermal Roll</option>
                        </Field>
                      </Col>
                      {values.paperType === "sheet" ? (
                        <Col md={4}>
                          <BootstrapForm.Label className="small fw-bold text-muted">Page Size</BootstrapForm.Label>
                          <Field as={BootstrapForm.Select} name="pageSize" className="form-select">
                            <option value="A4">A4</option>
                            <option value="A5">A5</option>
                            <option value="USLetter">US Letter</option>
                            <option value="Custom">Custom</option>
                          </Field>
                        </Col>
                      ) : (
                        <Col md={4}>
                          <BootstrapForm.Label className="small fw-bold text-muted">Roll Width</BootstrapForm.Label>
                          <InputGroup>
                            <Field name="rollWidth" type="number" as={BootstrapForm.Control} />
                            <InputGroup.Text>mm</InputGroup.Text>
                          </InputGroup>
                        </Col>
                      )}
                      <Col md={4}>
                        <BootstrapForm.Label className="small fw-bold text-muted">Columns</BootstrapForm.Label>
                        <Field name="gridColumns" type="number" min="1" max="10" as={BootstrapForm.Control} />
                      </Col>
                    </Row>

                    <div className="border-top pt-3">
                      <label className="small fw-bold text-muted text-uppercase mb-3 d-block">Single Label Metrics</label>
                      <Row className="g-3">
                        <Col xs={6} md={3}>
                          <BootstrapForm.Label className="small text-muted">Width</BootstrapForm.Label>
                          <InputGroup size="sm">
                            <Field name="labelWidth" type="number" as={BootstrapForm.Control} />
                            <InputGroup.Text>mm</InputGroup.Text>
                          </InputGroup>
                        </Col>
                        <Col xs={6} md={3}>
                          <BootstrapForm.Label className="small text-muted">Height</BootstrapForm.Label>
                          <InputGroup size="sm">
                            <Field name="labelHeight" type="number" as={BootstrapForm.Control} />
                            <InputGroup.Text>mm</InputGroup.Text>
                          </InputGroup>
                        </Col>
                        <Col xs={6} md={3}>
                          <BootstrapForm.Label className="small text-muted">Padding</BootstrapForm.Label>
                          <InputGroup size="sm">
                            <Field name="padding" type="number" as={BootstrapForm.Control} />
                            <InputGroup.Text>mm</InputGroup.Text>
                          </InputGroup>
                        </Col>
                        <Col xs={6} md={3}>
                          <BootstrapForm.Label className="small text-muted">Gap</BootstrapForm.Label>
                          <InputGroup size="sm">
                            <Field name="gap" type="number" as={BootstrapForm.Control} />
                            <InputGroup.Text>mm</InputGroup.Text>
                          </InputGroup>
                        </Col>
                      </Row>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* RIGHT COLUMN: FINE TUNING */}
              <Col lg={4}>
                {/* 4. TYPOGRAPHY */}
                <Card className="border-0 shadow-sm mb-4">
                  <Card.Header className="bg-white border-bottom py-3">
                    <h6 className="fw-bold m-0 d-flex align-items-center text-dark">
                      <FaFont className="me-2 text-warning" /> Typography
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-4 bg-light">
                    <div className="mb-3">
                      <label className="small fw-bold text-muted mb-1">Item Name Size</label>
                      <InputGroup size="sm">
                        <Field name="itemNameFontSize" type="number" as={BootstrapForm.Control} />
                        <InputGroup.Text>pt</InputGroup.Text>
                      </InputGroup>
                    </div>
                    <div className="mb-3">
                      <label className="small fw-bold text-muted mb-1">Price Font Size</label>
                      <InputGroup size="sm">
                        <Field name="priceFontSize" type="number" as={BootstrapForm.Control} />
                        <InputGroup.Text>pt</InputGroup.Text>
                      </InputGroup>
                    </div>
                    <div className="mb-3">
                      <label className="small fw-bold text-muted mb-1">Company Info Size</label>
                      <InputGroup size="sm">
                        <Field name="companyNameFontSize" type="number" as={BootstrapForm.Control} />
                        <InputGroup.Text>pt</InputGroup.Text>
                      </InputGroup>
                    </div>
                  </Card.Body>
                </Card>

                {/* 5. POSITIONING */}
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-white border-bottom py-3">
                    <h6 className="fw-bold m-0 d-flex align-items-center text-dark">
                      <FaLayerGroup className="me-2 text-danger" /> Layout Coordinates
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Row className="g-2 mb-2">
                      <Col xs={12} className="small fw-bold text-muted border-bottom pb-1 mb-2">Price Position</Col>
                      <Col xs={6}>
                        <InputGroup size="sm">
                          <InputGroup.Text>X</InputGroup.Text>
                          <Field name="priceX" as={BootstrapForm.Control} />
                        </InputGroup>
                      </Col>
                      <Col xs={6}>
                        <InputGroup size="sm">
                          <InputGroup.Text>Y</InputGroup.Text>
                          <Field name="priceY" as={BootstrapForm.Control} />
                        </InputGroup>
                      </Col>
                    </Row>
                    <Row className="g-2">
                      <Col xs={12} className="small fw-bold text-muted border-bottom pb-1 mb-2 mt-2">Company Position</Col>
                      <Col xs={6}>
                        <InputGroup size="sm">
                          <InputGroup.Text>X</InputGroup.Text>
                          <Field name="companyNameX" as={BootstrapForm.Control} />
                        </InputGroup>
                      </Col>
                      <Col xs={6}>
                        <InputGroup size="sm">
                          <InputGroup.Text>Y</InputGroup.Text>
                          <Field name="companyNameY" as={BootstrapForm.Control} />
                        </InputGroup>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default BarcodeSettingsForm;